import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, ClipboardList } from "lucide-react";

export default function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);
  const [singleName, setSingleName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setStaff(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addSingle = async (e) => {
    e.preventDefault();
    const name = singleName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/staff", { name });
      toast.success(`Added ${name}`);
      setSingleName("");
      await load();
    } catch {
      toast.error("Could not add");
    } finally {
      setBusy(false);
    }
  };

  const addBulk = async () => {
    // Split on commas or newlines
    const names = bulkText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) {
      toast.error("Paste a comma- or line-separated list first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/bulk", { names });
      toast.success(`Added ${data.added} · Skipped ${data.skipped} (already existed)`);
      setBulkText("");
      await load();
    } catch {
      toast.error("Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from your staff list?`)) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Removed");
      await load();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="neu-card p-6 md:p-8" data-testid="staff-manager">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-5 h-5 text-neu-accent" />
        <div className="heading text-lg text-[#3F4E5B]">Attending staff</div>
      </div>
      <p className="text-sm subtle mb-6">
        The attendings you work with. These populate the dropdown on the Log Case form.
      </p>

      <form onSubmit={addSingle} className="flex items-stretch gap-2 mb-4" data-testid="staff-single-form">
        <input
          type="text"
          value={singleName}
          onChange={(e) => setSingleName(e.target.value)}
          placeholder="Add one attending, e.g. Aaron Liddell"
          className="neu-input flex-1"
          data-testid="staff-single-name"
        />
        <button
          type="submit"
          disabled={busy || !singleName.trim()}
          className="btn-accent inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="staff-single-save"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      <details className="mb-6">
        <summary className="text-sm font-medium cursor-pointer inline-flex items-center gap-2 text-neu-accent">
          <ClipboardList className="w-4 h-4" /> Bulk paste (comma or newline separated)
        </summary>
        <div className="mt-3 space-y-3 animate-fade-in">
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"Aaron Liddell, Matthew Alleman, Michael Hale…\n\nor one name per line"}
            rows={5}
            className="neu-input py-3 resize-none"
            style={{ height: "auto", minHeight: "8rem" }}
            data-testid="staff-bulk-text"
          />
          <button
            type="button"
            onClick={addBulk}
            disabled={busy || !bulkText.trim()}
            className="btn-accent inline-flex items-center gap-2"
            data-testid="staff-bulk-save"
          >
            {busy ? "Adding…" : "Add all"}
          </button>
        </div>
      </details>

      <div className="text-xs subtle uppercase tracking-wider mb-3">
        {loading ? "Loading…" : `${staff.length} attending${staff.length === 1 ? "" : "s"}`}
      </div>
      {!loading && staff.length === 0 && (
        <div className="text-center py-6 neu-inset text-sm subtle">
          No staff yet — add your first attending above.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {staff.map((s) => (
          <div
            key={s.id}
            className="neu-inset p-3 flex items-center justify-between gap-3"
            data-testid={`staff-${s.id}`}
          >
            <span className="font-medium text-neu-text truncate">{s.name}</span>
            <button
              onClick={() => remove(s.id, s.name)}
              className="text-neu-muted hover:text-red-600 transition-colors p-1"
              aria-label={`Remove ${s.name}`}
              data-testid={`staff-remove-${s.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
